/// <amd-module name="WidgetCommon/WitUtils/WiqlUrlBuilder" />
import { getProject } from "Tfs/Widget/Context";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssLocationService } from "VSS/Platform/Location";
import { daysBetweenDates, shiftToUtc } from "WidgetCommon/DateUtils/DateUtils";
import { LocalStorageScope, LocalStorageWrapper } from "./LocalStorage/LocalStorageWrapper";
import { TempQueryUtils } from "./TempQueryUtils";
import * as UrlUtils from "./UrlUtils";

/**
 * Helpers for dealing with creating a server side temporary wiql query object.
 * This is a fallback for when wiql urls are too long and batch mode is necessary.
 */
export class WiqlURLBuilder {
    private static readonly COPY_QUERY_URL_DAYS_TO_EXPIRE = 90;
    public static readonly storageKey = "WitWidgetTempQuery";

    private _createdNewTempQueryId = false;

    public constructor(private context: IVssPageContext) {}

    public async BuildQueryURL(wiql: string, queryName: string, teamId: string): Promise<string> {
        const queryRoute = "ms.vss-work-web.query-search:route";
        const locationService = this.context.getService<IVssLocationService>("IVssLocationService");
        const project = getProject(this.context);
        const url = locationService.routeUrl(queryRoute, {
            project: project.name,
            wiql: wiql,
            name: queryName
        });

        if (!this.isUrlWithinConstraints(url)) {
            try {
                const tempQueryId = await this._buildTempQueryId(wiql, queryName, teamId);
                return Promise.resolve(UrlUtils.getTempQueryUrl(this.context, project.name, tempQueryId));
            } catch (error) {
                return Promise.reject(error);
            }
        } else {
            return Promise.resolve(url);
        }
    }

    /**
     * Create a tempQueryId with the wiql if there isn't one in the browser LocalStorage
     * @param wiql - the wiql for creating the query
     */
    public _buildTempQueryId(wiql: string, queryName: string, teamId: string): Promise<string> {
        const previousData = this._getWitLocalStorageInfo(teamId);
        let needNewTempQuery = true;
        this._createdNewTempQueryId = false;

        // There are 2 conditions that would cause us to create a new temp QueryID.
        // 1. If the the storeWiql is different than our wiql.. -> it means it either we hadn't create tempQueryId yet, or the process template changed.
        // 2. The stored timestamp is longer than the 90 days time frame, we would create a new temp query.
        if (previousData.Wiql === wiql) {
            // There is not easy way to determine when a tempquery expired after than making a query and check whether it return a 404 or not.
            // So, we are keeping track with a timestamp ourselves
            const startDate = shiftToUtc(new Date(Number(previousData.queryTimeStamp)));
            const currentDate = shiftToUtc(new Date());
            if (daysBetweenDates(startDate, currentDate, true) < WiqlURLBuilder.COPY_QUERY_URL_DAYS_TO_EXPIRE) {
                needNewTempQuery = false;
            }
        }

        const successCallback = (tempQueryId: string) => {
            this.setWitLocalStorageInfo({
                    Wiql: wiql,
                    tempQueryId: tempQueryId,
                    queryTimeStamp: new Date().getTime().toString()
                } as WiqlTempQueryLocalStorageInfo,
                teamId
            );
            this._createdNewTempQueryId = true;
            return tempQueryId;
        };
        const errorCallback = (error: Error) => {
            return error.message;
        };

        if (needNewTempQuery) {
            return TempQueryUtils.beginCreateTemporaryQueryId(this.context, wiql, queryName).then(successCallback, errorCallback);
        } else {
            return Promise.resolve(previousData.tempQueryId);
        }
    }

    /**
     * A method to check did a new TempQueryID had been created after execute the _buildTempQueryId method
     */
    public newTempQueryCreated(): boolean {
        return this._createdNewTempQueryId;
    }

    public _getWitLocalStorageInfo(teamId: string): WiqlTempQueryLocalStorageInfo {
        const standardData = {
            Wiql: "undefined",
            tempQueryId: "undefined",
            queryTimeStamp: new Date().getTime().toString()
        } as WiqlTempQueryLocalStorageInfo;
        const data = this.getLocalStorageWrapper(teamId).read(WiqlURLBuilder.storageKey, undefined, LocalStorageScope.Project);

        if (data === undefined) {
            return standardData;
        } else {
            try {
                return JSON.parse(data) as WiqlTempQueryLocalStorageInfo;
            } catch (e) {
                return standardData;
            }
        }
    }

    /**
     * Port from Vssf/WebPlatform/Platform/Scripts/VSS/Utils/Url.ts
     * Verifies that the given url is within the constraints of 2000 characters.
     *
     * @param url The url to verify.
     * @returns {boolean}
     */
    private isUrlWithinConstraints(url: string): boolean {
        return url.length <= 2000;
    }

    private setWitLocalStorageInfo(data: WiqlTempQueryLocalStorageInfo, teamId: string): void {
        this.getLocalStorageWrapper(teamId).write(WiqlURLBuilder.storageKey, JSON.stringify(data), LocalStorageScope.Project);
    }

    private getLocalStorageWrapper(teamId: string): LocalStorageWrapper {

        return new LocalStorageWrapper({
            projectId: getProject(this.context).id,
            teamId: teamId
        });
    }
}

export interface WiqlTempQueryLocalStorageInfo {
    // The actual wiql for the query
    Wiql: string;

    // The tempQueryId created before
    tempQueryId: string;

    // the time stamp when the query was created
    queryTimeStamp: string;
}
